import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Shield, CheckCircle2,
    Wrench, TrendingUp, Building,
    Lock, ChevronRight, X, Briefcase, Zap,
    Check, BarChart3,
    User, Info, LayoutDashboard, FileCheck, FileText, Wallet,
    ShieldCheck, Users
} from 'lucide-react';
import { useNavigate, useLocation, Link } from '../../routes';
import { useToast } from './ToastContext';
import { formatErrorMessage, getErrorWithAction, getPostHogInstance } from '@repo/shared-utils';
import { ROUTES } from '@repo/shared-types/routeRegistry';
import { coreDomainsApi } from '../../services/api/core-domains';
import { authApi, type CompleteClerkOnboardingData } from '../../services/api/auth';
import { PRIVACY_POLICY_VERSION, TERMS_OF_SERVICE_VERSION } from '../../constants/legalVersions';
import { useAppStore } from '../../store';
import { SignupWizard } from '../onboarding/SignupWizard';
import { UserRole } from '@repo/shared-types';
import { getOnboardingIdentity, getPostOnboardingRoute } from '../../utils/onboardingIdentity';
import { toOnboardingLeadStepData } from '../../utils/onboardingLeadPayload';
import { writeHistory } from '../../navigation/writeHistory';
import { isDemoMode } from '../../utils/demoClock';
/** Join the Ecosystem: Tenant and Vendor are invite-only (not on landing); Owner path shows fork next. */
type JoinRole = 'property-manager' | 'property-owner' | 'tenant' | 'investor';

const JOIN_ROLE_CARDS: { id: JoinRole; role: UserRole; label: string; description: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: 'property-manager', role: UserRole.PROPERTY_MANAGER, label: 'Property Manager', description: 'Full back-office management with compliance tools', icon: Briefcase },
    { id: 'property-owner', role: UserRole.OWNER, label: 'Property Owner', description: 'Manage your own properties or work with a PM', icon: User },
    { id: 'investor', role: UserRole.INVESTOR, label: 'Investor', description: 'Investment tracking and distribution management', icon: TrendingUp },
];

/** Hover blurb per role for the initial role picker (info icon). */
const ROLE_INFO_BLURB: Record<JoinRole, string> = {
    'property-manager':
        "We'll collect your contact and company details, license information, and how you'd like to get started. After that you'll set a password and sign in. You can add properties right after signup or later from the Property Directory.",
    'property-owner':
        "We'll collect your property details, contact and bank information, and set up your portal access. After that you'll create your account and sign in.",
    investor:
        "We're not accepting direct investor sign-ups at this time. Share your interest and we'll be in touch when the investor program opens.",
    tenant: "Tenant onboarding is by invite only. Use the link from your landlord or application.",
};

/** Management approach for Property Owner (Figma Step 2). Set when user completes Owner fork or wizard step. */
export type OwnerManagementApproach = 'self-manage-all' | 'pm-for-all' | 'mixed';

const POST_SIGNUP_ONBOARDING_KEY = 'postSignUpNeedsOnboarding';

export const LandingView: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();
    const { setAuthUser } = useAppStore();
    const [showOnboardingModal, setShowOnboardingModal] = useState(false);
    const [selectedJoinRole, setSelectedJoinRole] = useState<JoinRole | null>(null);
    const [selectedOnboardingRole, setSelectedOnboardingRole] = useState<UserRole | null>(null);
    const [selectedOnboardingIsSelfManagingOwner, setSelectedOnboardingIsSelfManagingOwner] = useState(false);
    /** Owner fork or wizard Step 2; drives backend user/entity creation. */
    const [ownerManagementApproach, setOwnerManagementApproach] = useState<OwnerManagementApproach | null>(null);
    const [pmCreateAccountData, setPmCreateAccountData] = useState<{
        email: string;
        firstName: string;
        lastName: string;
        companyName?: string;
        brokerLicenseNumber?: string;
        licenseState?: string;
        licenseExpiration?: string;
        isSelfManagingOwner?: boolean;
        preferredPmInviteCode?: string;
    } | null>(null);
    const [workflowPreferencesFromWizard, setWorkflowPreferencesFromWizard] = useState<{ monthlyReports?: boolean; maintenanceAlerts?: boolean } | null>(null);
    /** PM step data (e.g. trust-accounting) for Clerk/register payload. */
    const [lastPmOnboardingStepData, setLastPmOnboardingStepData] = useState<Record<string, unknown> | null>(null);
    const [scrolled, setScrolled] = useState(false);
    const [inviteData, setInviteData] = useState<{ applicationId?: string; email?: string; propertyId?: string; unitId?: string } | null>(null);
    /** Shown when user selects Investor: "We'll be in touch" (no direct sign-up). */
    const [showInvestorThankYou, setShowInvestorThankYou] = useState(false);
    const [joinClerkLegalConsent, setJoinClerkLegalConsent] = useState(false);
    const onboardingAbandonLatchRef = useRef<(() => void) | null>(null);
    const openOnboardingModalDeferred = useCallback(() => {
        // Avoid synchronous setState in an effect body.
        globalThis.setTimeout(() => setShowOnboardingModal(true), 0);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(globalThis.scrollY > 50);
        };
        globalThis.addEventListener('scroll', handleScroll);
        return () => globalThis.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('join') === '1') {
            document.getElementById('ecosystem')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            openOnboardingModalDeferred();
            const cleanSearch = '';
            const newUrl = location.pathname + (cleanSearch ? '?' + cleanSearch : '');
            writeHistory(() => {
                globalThis.history.replaceState(null, '', newUrl);
            });
        } else if (params.get('onboard') === 'vendor') {
            globalThis.setTimeout(() => {
                showToast(
                    'Vendor access is by invitation only. Your property manager adds you in Vendor Directory and sends an invite when you are ready to join.',
                    'info',
                );
            }, 0);
            const cleanSearch = '';
            const newUrl = location.pathname + (cleanSearch ? '?' + cleanSearch : '');
            writeHistory(() => {
                globalThis.history.replaceState(null, '', newUrl);
            });
        }
    }, [location.search, location.pathname, openOnboardingModalDeferred, showToast]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const token = params.get('tenantInvite');
        if (!token) return;
        let cancelled = false;
        coreDomainsApi.onboarding.getInviteByToken(token).then((payload) => {
            if (cancelled || !payload) return;
            setInviteData({
                applicationId: payload.applicationId ?? undefined,
                email: payload.email ?? undefined,
                propertyId: payload.propertyId ?? undefined,
                unitId: payload.unitId ?? undefined,
            });
            setSelectedJoinRole('tenant');
            setSelectedOnboardingRole(UserRole.TENANT);
            setShowOnboardingModal(true);
            const clean = new URLSearchParams(location.search);
            clean.delete('tenantInvite');
            const newSearch = clean.toString();
            const newUrl = location.pathname + (newSearch ? '?' + newSearch : '');
            writeHistory(() => {
                globalThis.history.replaceState(null, '', newUrl);
            });
        }).catch((err) => {
            showToast(
                'We could not load your invite link. Ask your landlord or property manager for a new link.',
                'error',
            );
            getPostHogInstance()?.capture?.('tenant_invite_load_failed', {
                message: err instanceof Error ? err.message : String(err),
            });
        });
        return () => { cancelled = true; };
    }, [location.search, location.pathname]);

    const handleOnboardingComplete = async (data: { role: string; stepData: Record<string, unknown>; completedAt: string }) => {
        const identity = getOnboardingIdentity(data.role, data.stepData);
        const { firstName, lastName, email } = identity;
        const name = [firstName, lastName].filter(Boolean).join(' ') || undefined;
        const company = (data.stepData?.company ?? {}) as Record<string, unknown>;
        const personal = (data.stepData?.personal ?? data.stepData?.entity) as Record<string, unknown> | undefined;
        const companyName = (typeof company?.companyName === 'string' ? company.companyName : undefined) ?? (typeof personal?.companyName === 'string' ? personal.companyName : undefined);
        const stepMgmt = (data.stepData?.['property-management-approach'] as { managementStructure?: OwnerManagementApproach } | undefined)?.managementStructure;
        const effectiveMgmt = stepMgmt ?? ownerManagementApproach;
        const workflowStep = data.stepData?.['workflow-preferences'] as { monthlyReports?: boolean; maintenanceAlerts?: boolean } | undefined;
        const workflowPrefs = (workflowStep && (workflowStep.monthlyReports !== undefined || workflowStep.maintenanceAlerts !== undefined))
            ? {
                ...(workflowStep.monthlyReports !== undefined && { monthlyReports: workflowStep.monthlyReports }),
                ...(workflowStep.maintenanceAlerts !== undefined && { maintenanceAlerts: workflowStep.maintenanceAlerts }),
            }
            : null;

        const validRole = data.role === 'PROPERTY_MANAGER' || data.role === 'OWNER' || data.role === 'VENDOR' || data.role === 'INVESTOR' || data.role === 'TENANT';
        if (validRole && sessionStorage.getItem(POST_SIGNUP_ONBOARDING_KEY) === '1') {
            sessionStorage.removeItem(POST_SIGNUP_ONBOARDING_KEY);
            const reviewTerms = (data.stepData?.review as { termsAccepted?: boolean } | undefined)?.termsAccepted === true;
            if (!reviewTerms) {
                showToast('Complete the review step and accept the Terms of Service and Privacy Policy to continue.', 'error');
                return;
            }
            const isOwner = data.role === 'OWNER';
            const role = (isOwner && (effectiveMgmt === 'self-manage-all' || effectiveMgmt === 'mixed'))
                ? 'PROPERTY_MANAGER'
                : (data.role as CompleteClerkOnboardingData['role']);
            const stepDataForPayload =
                data.stepData && typeof data.stepData === 'object'
                    ? (() => {
                        const copy = { ...data.stepData } as Record<string, unknown>;
                        delete copy['trust-accounting'];
                        return Object.keys(copy).length > 0 ? copy : undefined;
                    })()
                    : undefined;
            const payload: CompleteClerkOnboardingData = {
                role,
                isSelfManagingOwner: (isOwner && (effectiveMgmt === 'self-manage-all' || effectiveMgmt === 'mixed')) || (selectedOnboardingIsSelfManagingOwner ?? false),
                identity: { firstName: firstName || undefined, lastName: lastName || undefined, email: email ?? undefined },
                ...(companyName && { companyName }),
                ...(isOwner && effectiveMgmt && { managementApproach: effectiveMgmt }),
                ...(workflowPrefs && Object.keys(workflowPrefs).length > 0 && { workflowPreferences: workflowPrefs }),
                ...(stepDataForPayload && { stepData: stepDataForPayload }),
                onboardingConsent: {
                    termsVersion: TERMS_OF_SERVICE_VERSION,
                    privacyVersion: PRIVACY_POLICY_VERSION,
                    source: 'join_post_signup_wizard_review',
                    termsAccepted: true,
                },
            };
            const needsPlaidForPm = role === 'PROPERTY_MANAGER' && !payload.isSelfManagingOwner;
            try {
                if (needsPlaidForPm) {
                    sessionStorage.setItem('pendingClerkOnboarding', JSON.stringify(payload));
                    closeOnboardingModal();
                    navigate(ROUTES.COMPLETE_ONBOARDING, { replace: true });
                    return;
                }
                const user = await authApi.completeClerkOnboarding(payload);
                getPostHogInstance()?.capture?.('join_wizard_completed_post_signup', { role: user.role });
                setAuthUser(user.id, user.role as UserRole, user.isSelfManagingOwner ?? false);
                closeOnboardingModal();
                showToast('Account set up. Welcome!', 'success');
                const targetRoute = getPostOnboardingRoute(role, payload.isSelfManagingOwner);
                navigate(targetRoute, { replace: true });
            } catch (err) {
                showToast(formatErrorMessage(getErrorWithAction(err)), 'error');
            }
            return;
        }

        try {
            await coreDomainsApi.onboarding.create({
                role: data.role,
                stepData: toOnboardingLeadStepData(data.stepData),
                email,
                name,
                source: 'landing_modal',
                isSelfManagingOwner: selectedOnboardingIsSelfManagingOwner,
                ...(effectiveMgmt && { managementApproach: effectiveMgmt }),
            });
            if (workflowPrefs && Object.keys(workflowPrefs).length > 0) setWorkflowPreferencesFromWizard(workflowPrefs);
            if (data.role === UserRole.PROPERTY_MANAGER || data.role === UserRole.OWNER || data.role === UserRole.VENDOR || (data.role === UserRole.TENANT && inviteData)) {
                setLastPmOnboardingStepData(data.stepData ?? null);
            }
            if (data.role === UserRole.PROPERTY_MANAGER) {
                const license = (data.stepData?.license ?? {}) as Record<string, unknown>;
                setPmCreateAccountData({
                    email: email ?? '',
                    firstName: firstName || 'User',
                    lastName: lastName || 'User',
                    ...(companyName ? { companyName } : {}),
                    ...(typeof license?.licenseNumber === 'string' && license.licenseNumber.trim() && { brokerLicenseNumber: (license.licenseNumber).trim() }),
                    ...(typeof license?.licenseState === 'string' && license.licenseState.trim() && { licenseState: (license.licenseState).trim() }),
                    ...(typeof license?.licenseExpiration === 'string' && license.licenseExpiration.trim() && { licenseExpiration: (license.licenseExpiration).trim() }),
                    isSelfManagingOwner: selectedOnboardingIsSelfManagingOwner,
                });
            } else if (data.role === UserRole.OWNER) {
                setOwnerManagementApproach(effectiveMgmt ?? null);
                const ownerMgmtStep = data.stepData?.['property-management-approach'] as { inviteCode?: string; searchPM?: string } | undefined;
                setPmCreateAccountData({
                    email: email ?? '',
                    firstName: firstName || 'User',
                    lastName: lastName || 'User',
                    isSelfManagingOwner: effectiveMgmt === 'self-manage-all' || effectiveMgmt === 'mixed',
                    ...(typeof ownerMgmtStep?.inviteCode === 'string' && ownerMgmtStep.inviteCode.trim() && { preferredPmInviteCode: ownerMgmtStep.inviteCode.trim() }),
                });
            } else if (data.role === UserRole.VENDOR) {
                setPmCreateAccountData({
                    email: email ?? '',
                    firstName: firstName || 'User',
                    lastName: lastName || 'User',
                });
            } else if (data.role === UserRole.TENANT && inviteData) {
                setLastPmOnboardingStepData(data.stepData ?? null);
                setPmCreateAccountData({
                    email: email ?? '',
                    firstName: firstName || 'User',
                    lastName: lastName || 'User',
                    isSelfManagingOwner: false,
                });
            } else {
                showToast('Thank you! Your signup was received. We\'ll be in touch.', 'success');
                setShowOnboardingModal(false);
                setSelectedOnboardingRole(null);
                setSelectedJoinRole(null);
                setOwnerManagementApproach(null);
                setWorkflowPreferencesFromWizard(null);
            }
        } catch (err) {
            const message = err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string'
                ? (err as { message: string }).message
                : null;
            showToast(message && message.length < 120 ? message : 'Signup failed. Please try again or contact support.', 'error');
        }
    };

    const closeOnboardingModal = () => {
        onboardingAbandonLatchRef.current?.();
        sessionStorage.removeItem(POST_SIGNUP_ONBOARDING_KEY);
        setJoinClerkLegalConsent(false);
        setShowOnboardingModal(false);
        setSelectedJoinRole(null);
        setSelectedOnboardingRole(null);
        setOwnerManagementApproach(null);
        setShowInvestorThankYou(false);
        setPmCreateAccountData(null);
        setWorkflowPreferencesFromWizard(null);
        setLastPmOnboardingStepData(null);
        setInviteData(null);
    };

    const selectJoinRoleCard = async (id: JoinRole) => {
        if (id === 'property-manager') {
            setSelectedOnboardingRole(UserRole.PROPERTY_MANAGER);
            setSelectedOnboardingIsSelfManagingOwner(false);
            setSelectedJoinRole(null);
            return;
        }
        if (id === 'property-owner') {
            setSelectedJoinRole('property-owner');
            return;
        }
        if (id === 'investor') {
            try {
                await coreDomainsApi.onboarding.create({
                    role: UserRole.INVESTOR,
                    stepData: {},
                    source: 'landing_modal',
                });
            } catch {
                // ignore
            }
            setShowInvestorThankYou(true);
            return;
        }
        if (id === 'tenant') {
            showToast('Tenant sign-up is by invite only. Use the link from your landlord or application.', 'info');
            return;
        }
    };

    const stats = [
        { label: "Improvement in lead-to-lease conversion", value: "85%", icon: Zap },
        { label: "Of PMs report no trust in legacy AI systems", value: "78%", icon: Shield },
        { label: "Rise in baseline vendor & contractor costs", value: "77%", icon: TrendingUp },
        { label: "Investors mark transparency as #1 criteria", value: "74%", icon: BarChart3 }
    ];

    const featureCards: { title: string; description: string; icon: React.ComponentType<{ className?: string }> }[] = [
        { title: "Property dashboard & financials", description: "Real-time property management with financial tracking, occupancy, and activity.", icon: LayoutDashboard },
        { title: "Work orders & maintenance", description: "Work orders, inspections, unit turns, and projects with SOP integration.", icon: Wrench },
        { title: "Vendor marketplace & performance", description: "Pre-vetted vendors, quality scoring, jobs, invoices, and gamification.", icon: Building },
        { title: "Compliance", description: "Automated compliance tracking, expiration checks, and violation alerts.", icon: FileCheck },
        { title: "Leasing & applications", description: "Applications, background checks, lease management, and guest cards.", icon: FileText },
        { title: "Accounting", description: "Receivables, bills, ledger, bank reconciliation, and payment processing.", icon: Wallet },
        { title: "Reporting & owner statements", description: "Custom reports, owner statements, and distribution workflows.", icon: BarChart3 },
        { title: "Investor portal", description: "Programs, portfolio view, distributions, and entity relationships.", icon: TrendingUp },
    ];

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 selection:bg-primary/20 font-sans overflow-x-hidden">
            {/* Ambient Noise Overlay - subtle on light */}
            <div className="fixed inset-0 opacity-[0.02] pointer-events-none z-50 mix-blend-multiply bg-[url('/noise.svg')]"></div>

            {/* Navigation */}
            <nav className={`fixed w-full z-40 transition-all duration-300 ${scrolled ? 'bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-b border-slate-200 dark:border-slate-700 shadow-sm' : 'bg-transparent'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        <div className="flex items-center gap-3">
                            <img src="/HPMP.png" alt="Hummingbird Logo" className="w-8 h-8 object-contain drop-shadow-[0_0_10px_rgba(140,82,255,0.4)]" />
                            <span className="text-xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">
                                Hummingbird PMP
                            </span>
                        </div>
                        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
                            <a href="#features" className="hover:text-primary transition-colors">Features</a>
                            <a href="#ecosystem" className="hover:text-primary transition-colors">Ecosystem</a>
                            <a href="#trust" className="hover:text-primary transition-colors">Trust & Security</a>
                        </div>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => {
                                    // On the demo subdomain (or local VITE_DEMO_MODE=1) go directly to the picker.
                                    // Elsewhere (production landing) send users to the configured demo subdomain.
                                    if (isDemoMode()) {
                                        navigate('/demo');
                                        return;
                                    }
                                    const demoUrl = import.meta.env.VITE_DEMO_URL ?? 'https://demo.hummingbirdpmp.com/demo';
                                    window.location.href = demoUrl;
                                }}
                                className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-primary border border-primary/30 bg-primary/5 hover:bg-primary/10 dark:text-primary transition-colors"
                            >
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                                </span>
                                Try the demo
                            </button>
                            <button
                                onClick={() => navigate(ROUTES.LOGIN)}
                                className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-sm font-medium transition-colors"
                            >
                                Login
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="relative pt-32 pb-24 lg:pt-48 lg:pb-24 overflow-hidden flex flex-col items-center justify-center min-h-[90vh]">

                {/* Background Effects - light */}
                <div className="absolute inset-0 z-0 pointer-events-none">
                    <div className="absolute inset-0 bg-slate-50 dark:bg-slate-900 z-0"></div>
                    <div className="absolute inset-0 bg-[url('/Background.png')] bg-cover bg-center opacity-55 [mask-image:radial-gradient(ellipse_90%_90%_at_50%_50%,black_30%,transparent_60%)] z-10 transition-opacity"></div>

                    {/* Glowing Orbs - very subtle on light */}
                    <div className="absolute top-[20%] left-[20%] w-[500px] h-[500px] bg-primary/8 rounded-full blur-[120px] mix-blend-multiply animate-blob z-20"></div>
                    <div className="absolute right-[15%] bottom-[10%] w-[450px] h-[450px] bg-secondary/6 rounded-full blur-[100px] mix-blend-multiply animate-blob animation-delay-2000 z-20"></div>
                </div>

                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-30 text-center flex flex-col items-center">

                    <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter mb-16 leading-[1.1] text-slate-900 dark:text-white animate-fade-in-up [animation-delay:100ms] max-w-5xl mx-auto">
                        Bringing luxury to everyday housing.
                        <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary/80 to-secondary pb-2 inline-block">
                            Modernization delivered. Friction eliminated.
                        </span>
                    </h1>

                    <p className="max-w-3xl mx-auto text-lg md:text-xl text-slate-800 dark:text-slate-300 mb-12 leading-loose animate-fade-in-up [animation-delay:200ms] tracking-wide [text-shadow:0_1px_2px_rgba(255,255,255,0.9)] dark:[text-shadow:none]">
                        Every community deserves the same quality of operations, maintenance, and vendor attention as luxury properties. We bring that standard to everyday housing—reducing friction for property managers, tenants, and investors.
                    </p>


                    {/* Brand Kicker */}
                    <div className="mt-12 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 text-xs sm:text-sm font-medium animate-fade-in-up [animation-delay:400ms] shadow-sm mx-auto">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary"></span>
                        </span>
                        Luxury-grade operations for every community.
                    </div>
                </div>
            </div>

            {/* By The Numbers - Data Strip */}
            <div className="border-y border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 py-10 relative z-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 divide-x divide-slate-200">
                        {stats.map((stat, idx) => (
                            <div key={idx} className={`px-4 flex flex-col gap-2 ${idx === 0 || idx === 2 ? 'pl-0' : ''} ${idx === 0 ? 'md:pl-0' : 'md:pl-6'}`}>
                                <div className="flex items-center gap-2 text-primary">
                                    <stat.icon className="w-5 h-5" />
                                </div>
                                <div className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white mb-1">
                                    {stat.value}
                                </div>
                                <div className="text-sm text-slate-600 font-medium leading-snug pr-4">
                                    {stat.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Features - real product capabilities */}
            <div id="features" className="py-24 relative z-20 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 scroll-mt-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
<h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 tracking-tight text-slate-900 dark:text-white">
                                        Built for scale, designed for impact
                                    </h2>
                        <p className="text-slate-600 dark:text-slate-400 text-lg md:text-xl max-w-2xl mx-auto mb-6">
                            Every feature engineered to reduce friction and elevate standards in property management.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {featureCards.map((card) => {
                            const Icon = card.icon;
                            return (
                                <div
                                    key={card.title}
                                    className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 shadow-sm hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md transition-all flex flex-col"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 tracking-tight">
                                        {card.title}
                                    </h3>
                                    <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed flex-1">
                                        {card.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Vendor quality & certification */}
            <div id="ecosystem" className="py-24 relative overflow-hidden bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 scroll-mt-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 tracking-tight text-slate-900 dark:text-white">
                            Every Vendor on the Marketplace is Verified
                        </h2>
                        <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-6">
                            Licensed and insured for full-service work—or vetted by portfolio and track record for lite services. Bonded by request. Because everyday housing deserves luxury grade access.
                        </p>
                        <p className="text-primary font-semibold text-lg">
                            Quality + Verification = Investor-Grade Stability
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                        {[
                            { title: 'Pre-Vetted Quality', description: 'Vendors are screened for licenses and insurance where required, or verified by portfolio and work history for lite services.', icon: ShieldCheck },
                            { title: 'Quality Scoring', description: 'Gamification system rewards consistent performance and quality work through transparent metrics.', icon: BarChart3 },
                            { title: 'Reduced Liability', description: 'Comprehensive insurance and optional bonding protects property owners from risk and ensures quality work.', icon: Lock },
                            { title: 'Investor-Grade Stability', description: 'Certified vendors deliver consistent quality that meets investor and lender standards.', icon: Zap },
                        ].map((item) => (
                            <div key={item.title} className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 shadow-sm hover:border-slate-300 dark:hover:border-slate-600 transition-all">
                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                                    <item.icon className="w-5 h-5" />
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 tracking-tight">{item.title}</h3>
                                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{item.description}</p>
                            </div>
                        ))}
                    </div>
                    <div className="text-center bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-8 lg:p-12 shadow-sm">
                        <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">Join the Certified Marketplace</h3>
                        <p className="text-slate-600 dark:text-slate-400 max-w-xl mx-auto mb-6">
                            Grow your business with trusted property owners who value quality and reliability. Earn points, badges, and top marketplace visibility through consistent performance.
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                showToast(
                                    'Vendor onboarding is invite-only. Property managers add vendors from their workspace and send an invite when you are approved.',
                                    'info',
                                );
                            }}
                            className="px-6 py-3 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-all shadow-[0_0_15px_rgba(140,82,255,0.25)]"
                        >
                            Become a Certified Vendor
                        </button>
                    </div>
                </div>
            </div>

            {/* Building the Foundation for Trust */}
            <div id="trust" className="py-24 relative bg-slate-50 dark:bg-slate-800/50 border-y border-slate-200 dark:border-slate-700 scroll-mt-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="mb-12">
<h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-slate-900 dark:text-white tracking-tight">
                                            Building the Foundation for Trust
                                        </h2>
                        <p className="text-slate-600 dark:text-slate-400 text-lg max-w-2xl mx-auto">
                            We&apos;re building the infrastructure that institutional investors and professional property managers demand. Every feature designed for enterprise-grade reliability.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                        {[
                            { badge: 'Pre-Vetted', title: 'Certified Vendor Network', description: 'Licensed, insured, and quality-assessed service providers.', icon: Users },
                            { badge: 'Real-Time', title: 'Compliance Monitoring', description: 'Automated tracking and violation detection for regulatory compliance.', icon: Briefcase },
                            { badge: 'Investor-Grade', title: 'Analytics & Reporting', description: 'Portfolio-level insights and ESG scoring for institutional decision-making.', icon: BarChart3 },
                        ].map((item) => (
                            <div key={item.title} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 shadow-sm hover:border-slate-300 dark:hover:border-slate-600 transition-all text-left">
                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                                    <item.icon className="w-5 h-5" />
                                </div>
                                <p className="text-primary font-bold text-sm uppercase tracking-wider mb-1">{item.badge}</p>
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{item.description}</p>
                            </div>
                        ))}
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Security & Compliance</h3>
                        <p className="text-slate-600 dark:text-slate-400 text-sm mb-6">Enterprise-grade security standards in our roadmap.</p>
                        <div className="flex flex-wrap gap-4 justify-center">
                            {[
                                { name: 'SOC 2', status: 'Planned' },
                                { name: 'GDPR', status: 'Planned' },
                                { name: 'ISO 27001', status: 'Planned' },
                            ].map((cert) => (
                                <div key={cert.name} className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 shadow-sm">
                                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                                    <div>
                                        <span className="font-bold text-slate-900 dark:text-white">{cert.name}</span>
                                        <span className="text-slate-600 dark:text-slate-400 text-sm ml-2">{cert.status}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-3">
                        <img src="/HPMP.png" alt="Hummingbird Logo" className="w-6 h-6 object-contain opacity-70 hover:opacity-100 transition-all" />
                        <span className="text-sm font-bold text-slate-600 dark:text-slate-400">Hummingbird PMP</span>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-slate-600 dark:text-slate-400 font-medium">
                        <button type="button" onClick={() => navigate(ROUTES.LEGAL_PRIVACY)} className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors bg-transparent border-0 p-0 cursor-pointer font-medium">Privacy</button>
                        <button type="button" onClick={() => navigate(ROUTES.LEGAL_TERMS)} className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors bg-transparent border-0 p-0 cursor-pointer font-medium">Terms</button>
                        <a href="mailto:hello@hummingbirdpmp.com" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">Contact</a>
                    </div>
                    <p className="text-slate-500 dark:text-slate-500 text-sm font-medium">
                        &copy; {new Date().getFullYear()} Hummingbird PMP. Built for the Ecosystem.
                    </p>
                </div>
            </footer>

            {/* Sign up by role modal (role picker + SignupWizard; PM gets Create account step) */}
            {showOnboardingModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in px-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto flex flex-col animate-slide-up" onClick={e => e.stopPropagation()}>
                        {(() => {
                            const step1Complete = selectedOnboardingRole !== null;
                            const OnboardingStepper = () => (
                                <div className="flex items-center justify-center gap-2 px-6 pt-5 pb-2 border-b border-slate-200">
                                    <div className="flex items-center">
                                        <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${step1Complete ? 'bg-brand-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                                            {step1Complete ? <Check size={16} /> : '1'}
                                        </div>
                                        <span className={`ml-2 text-sm font-medium ${step1Complete ? 'text-slate-500' : 'text-brand-600'}`}>Role</span>
                                    </div>
                                    <div className="w-12 h-0.5 bg-slate-200 rounded" />
                                    <div className="flex items-center">
                                        <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${step1Complete ? 'bg-brand-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                                            2
                                        </div>
                                        <span className={`ml-2 text-sm font-medium ${step1Complete ? 'text-brand-600' : 'text-slate-400'}`}>Your Info</span>
                                    </div>
                                </div>
                            );
                            return showInvestorThankYou ? (
                                <div className="flex flex-col p-6">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">Thanks for your interest</h3>
                                        <button type="button" onClick={() => { setShowInvestorThankYou(false); closeOnboardingModal(); }} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-700 transition">
                                            <X size={20} />
                                        </button>
                                    </div>
                                    <p className="text-slate-600 dark:text-slate-400 mb-6">
                                        We&apos;re not accepting direct investor sign-ups at this time. We&apos;ve noted your interest and will be in touch when the investor program opens.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => { setShowInvestorThankYou(false); closeOnboardingModal(); }}
                                        className="px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium transition w-fit"
                                    >
                                        Close
                                    </button>
                                </div>
                            ) : inviteData ? (
                                <SignupWizard
                                    embedded
                                    isOpen={true}
                                    onClose={closeOnboardingModal}
                                    role={UserRole.TENANT}
                                    applicationId={inviteData.applicationId}
                                    isSelfManagingOwner={false}
                                    initialManagementApproach={undefined}
                                    onBack={closeOnboardingModal}
                                    onComplete={(data) => handleOnboardingComplete(data)}
                                    onboardingAbandonLatchRef={onboardingAbandonLatchRef}
                                />
                            ) : pmCreateAccountData ? (
                                <div className="flex flex-col">
                                    <OnboardingStepper />
                                    <div className="p-6 border-b border-slate-200 flex justify-between items-center">
                                        <h3 className="text-xl font-bold text-slate-900">Create your account</h3>
                                        <button type="button" onClick={closeOnboardingModal} className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition">
                                            <X size={20} />
                                        </button>
                                    </div>
                                    <div className="p-6 space-y-4 overflow-y-auto">
                                        <p className="text-slate-600 text-sm">Your signup was received. Continue with Clerk to create your account and sign in.</p>
                                        {pmCreateAccountData.companyName && (
                                            <p className="text-slate-700 text-sm font-medium">Company: {pmCreateAccountData.companyName}</p>
                                        )}
                                        <p className="text-slate-600 text-sm">You will set your password on the next screen.</p>
                                        <label htmlFor="join-clerk-legal-consent" className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                                            <input
                                                id="join-clerk-legal-consent"
                                                type="checkbox"
                                                className="mt-1 rounded border-slate-300"
                                                checked={joinClerkLegalConsent}
                                                onChange={(e) => setJoinClerkLegalConsent(e.target.checked)}
                                            />
                                            <span>
                                                I agree to the{' '}
                                                <Link to={ROUTES.LEGAL_TERMS} className="text-brand-600 hover:underline font-medium">
                                                    Terms of Service
                                                </Link>{' '}
                                                and{' '}
                                                <Link to={ROUTES.LEGAL_PRIVACY} className="text-brand-600 hover:underline font-medium">
                                                    Privacy Policy
                                                </Link>
                                                .
                                            </span>
                                        </label>
                                    </div>
                                    <div className="p-6 pt-0 flex gap-3 justify-end border-t border-slate-200">
                                        <button type="button" onClick={closeOnboardingModal} className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-600 hover:text-slate-800 hover:bg-slate-50 transition text-sm font-medium">
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                try {
                                                    if (!joinClerkLegalConsent) {
                                                        showToast('Please accept the Terms of Service and Privacy Policy to continue.', 'error');
                                                        return;
                                                    }
                                                    const isOwner = selectedOnboardingRole === UserRole.OWNER;
                                                    const role = isOwner && (ownerManagementApproach === 'self-manage-all' || ownerManagementApproach === 'mixed')
                                                        ? 'PROPERTY_MANAGER'
                                                        : (selectedOnboardingRole ?? 'PROPERTY_MANAGER');
                                                    const inviteAppId = (inviteData as { applicationId?: string } | null)?.applicationId;
                                                    const stepDataPayload = (lastPmOnboardingStepData && Object.keys(lastPmOnboardingStepData).length > 0)
                                                        ? { ...lastPmOnboardingStepData, ...(inviteAppId && { applicationId: inviteAppId }) }
                                                        : (inviteAppId ? { applicationId: inviteAppId } : undefined);
                                                    const id = getOnboardingIdentity(role as string, stepDataPayload ?? {});
                                                    sessionStorage.setItem('pendingClerkOnboarding', JSON.stringify({
                                                        role,
                                                        isSelfManagingOwner: pmCreateAccountData?.isSelfManagingOwner ?? false,
                                                        identity: { firstName: id.firstName || undefined, lastName: id.lastName || undefined, email: id.email },
                                                        ...(pmCreateAccountData?.companyName && { companyName: pmCreateAccountData.companyName }),
                                                        ...(isOwner && ownerManagementApproach && { managementApproach: ownerManagementApproach }),
                                                        ...(workflowPreferencesFromWizard && Object.keys(workflowPreferencesFromWizard).length > 0 && { workflowPreferences: workflowPreferencesFromWizard }),
                                                        ...(stepDataPayload && Object.keys(stepDataPayload).length > 0 && { stepData: stepDataPayload }),
                                                        onboardingConsent: {
                                                            termsVersion: TERMS_OF_SERVICE_VERSION,
                                                            privacyVersion: PRIVACY_POLICY_VERSION,
                                                            source: 'join_pm_landing_cta_checkbox',
                                                            termsAccepted: true,
                                                        },
                                                    }));
                                                    navigate(ROUTES.SIGN_UP);
                                                } catch (err) {
                                                    showToast(formatErrorMessage(getErrorWithAction(err)), 'error');
                                                }
                                            }}
                                            className="px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium transition"
                                        >
                                            Continue with Clerk
                                        </button>
                                    </div>
                                </div>
                        ) : selectedJoinRole === 'property-owner' && selectedOnboardingRole === null ? (
                            <>
                                <OnboardingStepper />
                                <div className="p-6 border-b border-slate-200 flex justify-between items-center">
                                    <h3 className="text-xl font-bold text-slate-900">How do you manage your portfolio?</h3>
                                    <button type="button" onClick={closeOnboardingModal} className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition">
                                        <X size={20} />
                                    </button>
                                </div>
                                <p className="px-6 pt-4 text-slate-600 text-sm">This helps us tailor your experience and provide the right tools.</p>
                                <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4 overflow-y-auto">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedOnboardingRole(UserRole.OWNER);
                                            setOwnerManagementApproach('pm-for-all');
                                            setSelectedJoinRole(null);
                                        }}
                                        className="flex items-center gap-4 p-3.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-slate-300 text-left text-slate-900 font-medium transition-all"
                                    >
                                        <div className="shrink-0 p-2 rounded-lg bg-emerald-500/20 text-emerald-600">
                                            <User className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                                            <span className="font-semibold">I hire a Property Manager</span>
                                            <span className="text-sm text-slate-600 font-normal">You work with a PM company and want oversight, reports, and approval workflows.</span>
                                        </div>
                                        <ChevronRight className="w-5 h-5 shrink-0 text-slate-400" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedOnboardingRole(UserRole.OWNER);
                                            setOwnerManagementApproach('self-manage-all');
                                            setSelectedJoinRole(null);
                                        }}
                                        className="flex items-center gap-4 p-3.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-slate-300 text-left text-slate-900 font-medium transition-all"
                                    >
                                        <div className="shrink-0 p-2 rounded-lg bg-purple-500/20 text-purple-600">
                                            <Building className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                                            <span className="font-semibold">I manage it myself</span>
                                            <span className="text-sm text-slate-600 font-normal">You handle your own property management including rent, maintenance, and screening.</span>
                                        </div>
                                        <ChevronRight className="w-5 h-5 shrink-0 text-slate-400" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedOnboardingRole(UserRole.OWNER);
                                            setOwnerManagementApproach('mixed');
                                            setSelectedJoinRole(null);
                                        }}
                                        className="flex items-center gap-4 p-3.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-slate-300 text-left text-slate-900 font-medium transition-all"
                                    >
                                        <div className="shrink-0 p-2 rounded-lg bg-amber-500/20 text-amber-600">
                                            <Building className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                                            <span className="font-semibold">Mixed</span>
                                            <span className="text-sm text-slate-600 font-normal">Some properties I manage myself, others I use a PM for.</span>
                                        </div>
                                        <ChevronRight className="w-5 h-5 shrink-0 text-slate-400" />
                                    </button>
                                </div>
                                <div className="px-6 pb-6">
                                    <button type="button" onClick={() => setSelectedJoinRole(null)} className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
                                        ← Back to role selection
                                    </button>
                                </div>
                            </>
                        ) : selectedOnboardingRole === null ? (
                            <>
                                <OnboardingStepper />
                                <div className="p-6 border-b border-slate-200 flex justify-between items-center">
                                    <h3 className="text-xl font-bold text-slate-900">Welcome to Hummingbird</h3>
                                    <button type="button" onClick={closeOnboardingModal} className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition">
                                        <X size={20} />
                                    </button>
                                </div>
                                <p className="px-6 pt-4 text-slate-600 text-sm">First, tell us who you are. We&apos;ll customize your experience.</p>
                                <div className="p-6 grid grid-cols-1 gap-3 overflow-y-auto max-h-[70vh]">
                                    {JOIN_ROLE_CARDS.map(({ id, label, description, icon: Icon }) => (
                                        <div key={id} className="flex gap-2 items-stretch">
                                            <button
                                                type="button"
                                                className="flex flex-1 min-w-0 items-center gap-4 p-3.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-slate-300 text-left text-slate-900 font-medium transition-all"
                                                onClick={() => void selectJoinRoleCard(id)}
                                            >
                                                <div className="shrink-0 p-2 rounded-lg bg-brand-500/15 text-brand-600">
                                                    <Icon className="w-5 h-5" />
                                                </div>
                                                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                                                    <span className="font-semibold">{label}</span>
                                                    <span className="text-sm text-slate-600 font-normal">{description}</span>
                                                </div>
                                                <ChevronRight className="w-5 h-5 shrink-0 text-slate-400" aria-hidden />
                                            </button>
                                            <div className="relative group shrink-0 self-center">
                                                <button
                                                    type="button"
                                                    className="p-2 rounded-md border-0 bg-transparent cursor-help text-slate-400 hover:text-slate-600"
                                                    aria-label={`What to expect: ${label}`}
                                                >
                                                    <Info className="w-4 h-4" aria-hidden />
                                                </button>
                                                <div className="absolute right-0 top-full mt-1.5 w-72 p-3 bg-slate-900 text-white text-xs leading-relaxed rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity z-50 pointer-events-none">
                                                    {ROLE_INFO_BLURB[id]}
                                                    <div className="absolute -top-1 right-4 border-4 border-transparent border-b-slate-900" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <SignupWizard
                                embedded
                                isOpen={true}
                                onClose={closeOnboardingModal}
                                role={selectedOnboardingRole}
                                isSelfManagingOwner={selectedOnboardingIsSelfManagingOwner}
                                initialManagementApproach={ownerManagementApproach ?? undefined}
                                onboardingAbandonLatchRef={onboardingAbandonLatchRef}
                                onBack={() => {
                                    if (selectedOnboardingRole === UserRole.OWNER) {
                                        setSelectedOnboardingRole(null);
                                        setOwnerManagementApproach(null);
                                        setSelectedJoinRole('property-owner');
                                    } else {
                                        setSelectedOnboardingRole(null);
                                        setSelectedJoinRole(null);
                                    }
                                }}
                                onComplete={(data) => handleOnboardingComplete(data)}
                                onClerkSignupRequest={selectedOnboardingRole === UserRole.PROPERTY_MANAGER && sessionStorage.getItem(POST_SIGNUP_ONBOARDING_KEY) !== '1' ? (payload: CompleteClerkOnboardingData) => {
                                    try {
                                        sessionStorage.setItem('pendingClerkOnboarding', JSON.stringify(payload));
                                        closeOnboardingModal();
                                        navigate(ROUTES.SIGN_UP);
                                    } catch (err) {
                                        showToast(formatErrorMessage(getErrorWithAction(err)), 'error');
                                    }
                                } : undefined}
                            />
                        );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
};

